import { procedures as schemaProcedures } from './schemas';
import apiClient from './apiClient';
import ProceduresDataStore from '../../datastores/ProceduresDataStore';
import { RegisterEmployabilityCharterRequest } from '@/domain/entities/procedures/RegisterEmployabilityCharterRequest';

class ProceduresDataStoreGraphQL implements ProceduresDataStore {
  client = apiClient();

  async getProceduresByStudent() {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProceduresByStudent
      });

      return response?.data?.procedures?.requests;
    } catch (error) {
      throw new Error('error getProceduresByStudent');
    }
  }

  async getProcedureInformation(procedureId: number, studentType: string) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProcedureInformation,
        variables: { procedureId, studentType }
      });

      return response?.data?.procedures?.procedure;
    } catch (error) {
      throw new Error('error getProcedureInformation');
    }
  }

  async getProcedureRequestDetail(procedureId: number, requestId: number) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProcedureRequestDetail,
        variables: { procedureId, requestId }
      });
      return response?.data?.procedures?.requestDetail;
    } catch (error) {
      throw new Error('error getStudentProcedureStatus');
    }
  }

  async getModalityOptionList(campus: string, periodId: number) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getModalityOptionList,
        variables: { campus, periodId }
      });
      return response?.data?.procedures?.modalityOptionList;
    } catch (error) {
      throw new Error('error getModalityOptionList');
    }
  }

  /* async getStudentProcedureStatus() {
     try {
       const response = await this.client.query({
         query: schemaProcedures.getStudentProcedureStatus
       });
       const { currentStatus } = response.data?.procedures;
       return currentStatus;
     } catch (error) {
       throw new Error('error getStudentProcedureStatus');
     }
   } */

  async getStudentInformation() {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getStudentInformation
      });
      return response?.data?.student?.procedureInfo;
    } catch (error) {
      throw new Error('error getStudentInformation');
    }
  }

  async getProceduresCategory() {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProceduresCategory
      });
      return response?.data?.procedures?.categories;
    } catch (error) {
      throw new Error('error getProceduresCategory');
    }
  }

  async getCampusByModality(modalityId: string) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getCampusByModality,
        variables: { modalityId }
      });
      return response?.data?.procedures?.campusByModality;
    } catch (error) {
      throw new Error('error getCampusByModality');
    }
  }

  async getProgramByCampus(campusId: string, modalityId: string) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProgramByCampus,
        variables: { campusId, modalityId }
      });
      return response?.data?.procedures?.programByCampus;
    } catch (error) {
      throw new Error('error getProceduresCategory');
    }
  }

  async getProceduresNotAutomated() {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProceduresNotAutomated
      });
      return response?.data?.procedures?.proceduresNotAutomated;
    } catch (error) {
      throw new Error('error getProceduresNotAutomated');
    }
  }

  async getProceduresCreated(year: any) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getProceduresCreated,
        variables: { year }
      });
      return response?.data?.procedures?.requestByYear;
    } catch (error) {
      throw new Error('error getProceduresCreated');
    }
  }

  async registerTicketNotAutomated(request: any) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProcedures.registerTicketNotAutomated,
        variables: { request }
      });
      return response?.data?.registerTicketCRM;
    } catch (error) {
      throw new Error('error registerTicketNotAutomated');
    }
  }

  async registerCarnet(request: any) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProcedures.registerCarnet,
        variables: { request }
      });
      return response.data;
    } catch (error) {
      throw new Error('error registerCarnet');
    }
  }

  async activateRegisterCarnet(
    requestId: number,
    procedureId: number,
    deliveryMode: string,
    career: string
  ) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProcedures.activateRegisterCarnet,
        variables: { requestId, procedureId, deliveryMode, career }
      });
      return response?.data?.activateCertificateOfStudiesProcedure;
    } catch (error) {
      throw new Error('error active register carnet');
    }
  }

  async cancelRegisterCarnet(requestId: number, procedureId: number) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProcedures.cancelRegisterCarnet,
        variables: { requestId, procedureId }
      });
      return response?.data?.cancelRequestProcedure;
    } catch (error) {
      throw new Error('error cancel register carnet');
    }
  }

  async getYearsOfProcedures() {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getYearsOfProcedures
      });
      return response?.data?.procedures?.years;
    } catch (error) {
      throw new Error('error getYearsOfProcedures');
    }
  }

  async registerChangePhoto(imgBase64: string, type: string, period: string) {
    try {
      const response = await this.client.mutate({
        mutation: schemaProcedures.registerChangePhoto,
        variables: { imgBase64, type, period }
      });
      return response?.data?.updatePhoto;
    } catch (error) {
      throw new Error('error registerChangePhoto');
    }
  }

  async registerEmployabilityCharter(
    request: RegisterEmployabilityCharterRequest
  ) {
    try {
      const {
        acadCareer,
        campus,
        charterDesc,
        charterType,
        companyName,
        duration,
        periodId,
        position,
        representative,
        studentType,
        periodRelativeCP
      } = request;
      const response = await this.client.mutate({
        mutation: schemaProcedures.registerEmployabilityCharter,
        variables: {
          request: {
            acadCareer,
            campus,
            charterDesc,
            charterType,
            companyName,
            duration,
            periodId,
            position,
            representative,
            studentType,
            periodRelativeCP
          }
        }
      });
      return response?.data?.registerEmployabilityCharter;
    } catch (error) {
      throw new Error('error registerChangePhoto');
    }
  }

  async getPeriodsWithDebts(currentPeriod: string) {
    try {
      const response = await this.client.query({
        query: schemaProcedures.getPeriodsWithDebts,
        variables: { currentPeriod }
      });

      return response?.data?.procedures?.periodsWithDebts;
    } catch (error) {
      throw new Error('error getPeriodsWithDebts');
    }
  }
}

export default ProceduresDataStoreGraphQL;
